import { useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  IconArrowLeft, IconArrowBackUp, IconCalendar, IconChartBar, IconChevronDown, IconClick, IconClock, IconDots, IconEye, IconFileText, IconHistory, IconInfoCircle, IconLock,
  IconMail, IconSend, IconTarget, IconUser, IconUsers, IconUsersGroup,
} from '@tabler/icons-react';
import { DataError, useList, useRecord } from '@hana/data';
import { useT } from '@hana/i18n';
import { Chip, Button, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { CAMPAIGN_STATUS_LABEL } from './logic';
import {
  HOURS, RECV_COLOR, RECV_LABEL, TL, TL_X, abRows, filterTabs, fmtPct, funnel, history, hotRows, infoRows, isSent, oppCount, oppSeed, recipients, replies, replyCount, timeline,
  type RecvFilter,
} from './campaignDetail';
import './campaignDetail.i18n';
import s from './campaignDetail.module.css';

// Mint-Marketing-Campaign (view=detail, 1920): trang chi tiết 2:1 — cột trái: Kết quả · Mở/bấm sau khi gửi · Giờ mở · Người nhận · Nội dung · Lịch sử;
// cột phải 380: Việc cần làm · Phản hồi · Thông tin chính · Người phụ trách · Liên kết. Chỉ đọc: nút ghi báo toast.

type Sec = 'm0' | 'm1' | 'm2' | 'm3' | 'm4' | 'm5' | 'm8' | 'rTodo' | 'm7' | 'rInfo' | 'rPpl' | 'rLink';
const OPEN0: Record<string, boolean> = { m1: true, m2: true, m3: false, m4: true, m5: true, m8: true, rTodo: true, m7: true, rInfo: true, rPpl: true, rLink: true };

function readState(): ScreenState | null {
  const v = new URLSearchParams(location.search).get('state');
  return v === 'loading' || v === 'empty' || v === 'error' || v === 'noperm' ? v : null;
}
const ini = (n: string) => n.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();

function Fold({ open, label, onClick }: { open: boolean; label: string; onClick: () => void }) {
  return (
    <button type="button" className={`${s.fold} ${open ? s.foldOn : ''}`} aria-expanded={open} aria-label={`Mở hoặc gập ${label}`} onClick={onClick}>
      <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />
    </button>
  );
}
function Tip({ text }: { text: string }) {
  return <span className={s.tip} role="img" tabIndex={0} aria-label={text} title={text}><IconInfoCircle size={14} stroke={1.9} aria-hidden="true" /></span>;
}

export function CampaignDetail() {
  const tx = useT();
  const toast = useToast();
  const navigate = useNavigate();
  const { campaignId } = useParams({ strict: false }) as { campaignId?: string };
  const camp = useRecord('mktCampaign', campaignId);
  const det = useRecord('mktCampaignDetail', campaignId);
  const all = useList('mktCampaign', { limit: 200 });
  const forced = readState();
  const [open, setOpen] = useState<Record<string, boolean>>(OPEN0);
  const [rf, setRf] = useState<RecvFilter>('all');
  const [marked, setMarked] = useState<Record<string, boolean>>({});
  const [opp] = useState<Record<string, boolean>>(oppSeed);
  const [showAll, setShowAll] = useState(false);
  const isOpen = (k: Sec) => !!open[k];
  const tog = (k: Sec) => () => setOpen((o) => ({ ...o, [k]: !o[k] }));
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const soon = (what: string) => () => toast({ msg: `${what} chưa mở ở bản này` });
  void all;

  let state: ScreenState = 'ready';
  if (forced) state = forced;
  else if (camp.isLoading || det.isLoading) state = 'loading';
  else if (camp.isError) state = camp.error instanceof DataError && camp.error.code === 'noperm' ? 'noperm' : 'error';
  else if (!camp.data || !det.data) state = 'empty';
  const c = camp.data;
  const d = det.data;
  const ready = state === 'ready' && !!c && !!d;

  const v = useMemo(() => {
    if (!c || !d) return null;
    const rows = recipients(c, d, marked);
    const oppN = oppCount(c.id, opp);
    const replyN = replyCount(c, rows);
    const tabs = filterTabs(c, d, rows, oppN);
    const tl = timeline(c);
    return { rows, oppN, replyN, tabs, tl, fun: funnel(c, replyN, oppN), reps: replies(c, rows, opp), hot: hotRows(rows, c.id, opp), ab: abRows(c), hist: history(c, d, oppN) };
  }, [c, d, marked, opp]);

  const sent = !!c && isSent(c);
  const mark = (sn: string) => () => setMarked((m) => ({ ...m, [sn]: !m[sn] }));
  const back = () => void navigate({ to: '/ezy/marketing' });

  const body = () => {
    if (!c || !d || !v) return null;
    const cur = v.tabs.find((t) => t.key === rf) ?? v.tabs[0]!;
    const list = v.rows.filter(cur.test);
    const saleT = d.sales.reduce((a, q) => a + q[1], 0) || 1;
    const SC5 = ['var(--d-blue)', 'var(--d-green)', 'var(--d-amber)', 'var(--d-violet)', 'var(--d-gray)'];
    const resend = sent && c.fail ? `Gửi lại ${c.fail} địa chỉ lỗi / bounce` : null;
    const todo = [
      ...(resend ? [{ k: resend, who: 'DAVID', due: 'sau quét thư dội', tone: 'err', act: 'Gửi lại' }] : []),
      ...v.reps.filter((r) => r.canOpp).slice(0, 3).map((r) => ({ k: `Tạo cơ hội · ${r.sn} trả lời`, who: r.sale, due: r.when, tone: 'pri', act: 'Tạo cơ hội' })),
    ];
    const todoRows = showAll ? todo : todo.slice(0, 4);
    const od = fmtPct(v.tl.oP);
    const oh = Math.round(c.sent ? (c.open / c.sent) * 100 : 0);
    const arcOff = (251.3 * (1 - Math.max(0, Math.min(1, c.sent ? c.open / c.sent : 0)))).toFixed(1);
    const yb = TL.H - TL.bot;
    const people: [string, string][] = [[c.owner, 'Người tạo chiến dịch'], ...d.sales.slice(0, 3).map(([k, n]) => [k, `Sale · ${n} khách nhận`] as [string, string])];
    const sec = (k: Sec, label: string, icon: ReactNode, tip: string, extra: ReactNode, content: ReactNode, region = label) => (
      <section className={s.card} aria-label={region}>
        <div className={s.head}>
          <span className={s.hIc}>{icon}</span>
          <h2 className={s.h2}>{label}</h2>
          <Tip text={tip} />
          <span className={s.grow} />
          {extra}
          <Fold open={isOpen(k)} label={label} onClick={tog(k)} />
        </div>
        {isOpen(k) && <div className={s.body}>{content}</div>}
      </section>
    );

    return (
      <div className={s.grid}>
        <div className={s.main}>
          {sent ? (
            <>
              {sec('m1', tx('cd.t1'), <IconChartBar size={18} stroke={1.9} aria-hidden="true" />, tx('cd.t1tip'), null,
                <div className={s.res}>
                  <div className={s.fun} role="img" aria-label={`Phễu ${c.id}: ` + v.fun.map((f) => `${f.k} ${f.n}`).join(', ')}>
                    {v.fun.map((f) => (
                      <div key={f.k} className={s.funR}>
                        <span className={s.funK}><span className={s.sw} style={{ background: f.c }} />{f.k}</span>
                        <div className={s.funBar}><div style={{ width: `${f.w}%`, background: f.c }} /></div>
                        <b className={s.funN}>{f.n}</b>
                        <span className={s.funP}>{f.p}</span>
                      </div>
                    ))}
                  </div>
                  <div className={s.gauge}>
                    <svg role="img" aria-label={`Tỷ lệ mở ${od} · lỗi ${c.fail} · huỷ đăng ký ${d.optout}`} width="170" height="93" viewBox="0 0 190 104">
                      <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" style={{ stroke: 'var(--line)' }} strokeWidth="14" strokeLinecap="round" />
                      <path d="M 15 95 A 80 80 0 0 1 175 95" fill="none" style={{ stroke: 'var(--d-blue)' }} strokeWidth="14" strokeLinecap="round" strokeDasharray="251.3 251.3" strokeDashoffset={arcOff} />
                      <text x="95" y="82" textAnchor="middle" fontSize="28" fontWeight="550" style={{ fill: 'var(--tx)' }} fontFamily="Inter, system-ui, sans-serif">{oh}%</text>
                    </svg>
                    <span>{tx('cd.1')}</span>
                    <div className={s.side}>
                      {([['Bounce', d.bounce, 'var(--err)'], ['Lỗi gửi', Math.max(0, c.fail - d.bounce), 'var(--err)'], ['Huỷ ĐK', d.optout, 'var(--d-violet)'], ['Bỏ qua', d.skipped, 'var(--d-gray)']] as const).map(([k, n, col]) => (
                        <div key={k} className={s.sideC}><div><span className={s.dot} style={{ background: col }} />{k}</div><b style={n && k === 'Bounce' ? { color: 'var(--err)' } : undefined}>{n}</b></div>
                      ))}
                    </div>
                  </div>
                </div>)}
              <div className={s.main}>
                {sec('m2', tx('cd.t2'), <IconClock size={18} stroke={1.9} aria-hidden="true" />, tx('cd.t2tip'),
                  <div className={s.legend} title="Mở / bấm tích luỹ theo giờ từ lúc gửi"><span><span style={{ width: 14, height: 3, borderRadius: 2, background: 'var(--d-blue)' }} />{tx('cd.2')}</span><span><span style={{ width: 14, height: 0, borderTop: '2px dashed var(--d-amber)' }} />{tx('cd.3')}</span></div>,
                  <svg role="img" aria-label={`Mở tích luỹ đạt ${od}, bấm ${fmtPct(v.tl.cP)} sau 72 giờ`} width="100%" height="210" viewBox="0 0 600 210" style={{ display: 'block' }}>
                    {v.tl.grid.map((g) => (
                      <g key={g.k}>
                        <line x1="44" x2="584" y1={g.y} y2={g.y} style={{ stroke: 'var(--line)' }} strokeWidth="1" />
                        <text x="36" y={g.ty} textAnchor="end" fontSize="12" style={{ fill: 'var(--lb)' }} fontFamily="Inter, system-ui, sans-serif">{g.k}</text>
                      </g>
                    ))}
                    <path d={v.tl.open.area} style={{ fill: 'var(--d-blue)' }} opacity="0.08" />
                    <path d={v.tl.open.d} fill="none" style={{ stroke: 'var(--d-blue)' }} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    <path d={v.tl.click.d} fill="none" style={{ stroke: 'var(--d-amber)' }} strokeWidth="2" strokeDasharray="5 5" />
                    <circle cx={v.tl.open.lx} cy={v.tl.open.ly} r="4.5" style={{ fill: 'var(--card)', stroke: 'var(--d-blue)' }} strokeWidth="2.5" />
                    <text x={v.tl.open.lx} y={v.tl.open.ly - 10} textAnchor="end" fontSize="12" fontWeight="550" style={{ fill: 'var(--tx)' }} fontFamily="Inter, system-ui, sans-serif">{od}</text>
                    <text x={v.tl.click.lx} y={v.tl.click.ly + 18} textAnchor="end" fontSize="12" fontWeight="550" style={{ fill: 'var(--warn)' }} fontFamily="Inter, system-ui, sans-serif">{fmtPct(v.tl.cP)}</text>
                    {v.tl.open.pts.map((p, i) => <text key={i} x={p[0]} y={yb + 24} textAnchor="middle" fontSize="12" style={{ fill: 'var(--lb)' }} fontFamily="Inter, system-ui, sans-serif">{TL_X[i]}</text>)}
                  </svg>, tx('cd.t2aria'))}
                {sec('m3', tx('cd.t3'), <IconClock size={18} stroke={1.9} aria-hidden="true" />, tx('cd.t3tip'), null,
                  <div className={s.hours} role="img" aria-label={'Giờ mở mail: đông nhất 9h, 8h và 10h · ' + HOURS.map(([k, n]) => `${k}h ${n}%`).join(', ')}>
                    {HOURS.map(([k, n]) => <div key={k} title={`${k}h: ${n}%`}><div style={{ height: `${n}%` }} /><span>{k}h</span></div>)}
                  </div>, 'Giờ mở mail')}
              </div>
            </>
          ) : (
            <section className={s.card} aria-label="Chưa gửi">
              <div className={s.row}><b>{c.status === 'SCHEDULED' ? `Hẹn gửi ${d.start}` : 'Chưa gửi'}</b><span className={s.tx2}>{c.status === 'SCHEDULED' ? `Gửi nền từ ${d.sender}` : 'Gửi thử cho mình rồi lên lịch'}</span></div>
            </section>
          )}

          {sec('m5', tx('cd.t5'), <IconUsers size={18} stroke={1.9} aria-hidden="true" />,
            `Hiện ${list.length} khách mẫu · tên ghi ShortName, không hiện email · trạng thái theo từng địa chỉ (pending · sent · fail · bounce · replied · skip · optout)`,
            <div className={s.rcSum}>
              <span title="Khách"><IconUsers size={14} aria-hidden="true" /><b>{d.customers || c.total}</b></span>
              <span title="Địa chỉ"><IconMail size={14} aria-hidden="true" /><b>{c.total}</b></span>
            </div>,
            <>
              <div className={s.rcTop}>
                <div>
                  <div className={s.rcLb}>{tx('cd.4')}</div>
                  <div className={s.chips}>{d.segments.map((g) => <span key={g} className={s.chip}>{g}</span>)}</div>
                </div>
                <div>
                  <div className={s.rcLb}><IconUsersGroup size={14} aria-hidden="true" />{tx('cd.5')}</div>
                  <div className={s.saleBar} role="img" aria-label={'Khách theo Sale: ' + d.sales.map(([k, n]) => `${k} ${n} (${Math.round((n / saleT) * 100)}%)`).join(', ')}>
                    {d.sales.map(([k, n], i) => <div key={k} style={{ flex: `${n} 1 0`, background: SC5[i % 5] }} />)}
                  </div>
                  <div className={s.saleL}>
                    {d.sales.map(([k, n], i) => <div key={k}><span className={s.dot} style={{ background: SC5[i % 5] }} /><span>{k}</span><b>{n}</b></div>)}
                  </div>
                </div>
              </div>
              <div role="radiogroup" aria-label="Lọc trạng thái người nhận" className={s.seg}>
                {v.tabs.map((t) => (
                  <button key={t.key} type="button" role="radio" aria-checked={cur.key === t.key} onClick={() => setRf(t.key)}>
                    <span className={s.dot} style={{ background: t.color }} />{t.label}<span>{t.n}</span>
                  </button>
                ))}
              </div>
              <table className={s.tbl}>
                <colgroup><col /><col style={{ width: 90 }} /><col style={{ width: 70 }} /><col style={{ width: 130 }} /><col style={{ width: 56 }} /><col style={{ width: 56 }} /><col style={{ width: 110 }} /><col style={{ width: 120 }} /></colgroup>
                <thead>
                  <tr>
                    <th scope="col">{tx('cd.6')}</th><th scope="col">Sale</th><th scope="col" className={s.r}>{tx('cd.7')}</th><th scope="col">{tx('cd.8')}</th>
                    <th scope="col" className={s.r}>{tx('cd.9')}</th><th scope="col" className={s.r}>{tx('cd.10')}</th><th scope="col">{tx('cd.11')}</th><th scope="col"><span style={{ display: 'none' }}>{tx('cd.12')}</span></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((r) => {
                    const has = !!opp[c.id + '|' + r.sn];
                    const a: { k: string; run: () => void; cls: string } | null =
                      r.s === 'reply' ? { k: has ? 'Có cơ hội' : 'Tạo cơ hội', run: noWrite, cls: has ? s.actOk! : s.actPri! }
                      : r.s === 'open' || r.s === 'click' ? { k: 'Đã trả lời', run: mark(r.sn), cls: '' }
                      : r.s === 'bounce' ? { k: 'Gỡ địa chỉ', run: noWrite, cls: '' }
                      : null;
                    return (
                      <tr key={r.sn}>
                        <td><b style={{ fontWeight: 'var(--fw-label)' }}>{r.sn}</b></td>
                        <td className={s.tx2}>{r.sale}</td>
                        <td className={`${s.tx2} ${s.r}`}>{r.addr}</td>
                        <td><span className={s.st}><span className={s.dot} style={{ background: RECV_COLOR[r.s] }} />{RECV_LABEL[r.s]}</span></td>
                        <td className={s.r}>{sent ? r.open : '—'}</td>
                        <td className={s.r}>{sent ? r.click : '—'}</td>
                        <td className={s.tx2}>{r.first}</td>
                        <td className={s.r}>{a && <button type="button" className={`${s.act} ${a.cls}`} onClick={a.run}>{a.k}</button>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>)}

          {sec('m4', tx('cd.t4'), <IconFileText size={18} stroke={1.9} aria-hidden="true" />, tx('cd.t4tip'), null,
            <div className={s.cont}>
              <button type="button" className={s.pv} aria-label="Xem đầy đủ mẫu thư" onClick={soon('Xem thử mẫu')}>
                <div className={s.pvH}>HANA LOGISTICS</div>
                <div className={s.pvB}>
                  <div className={s.lnT} style={{ width: '80%' }} />
                  <div className={s.ln} /><div className={s.ln} style={{ width: '92%' }} /><div className={s.ln} style={{ width: '96%' }} />
                  <div className={s.pvImg} />
                  <div className={s.ln} style={{ width: '88%' }} /><div className={s.ln} style={{ width: '70%' }} />
                  <div className={s.pvBtn} />
                  <div className={s.ln} style={{ width: '60%', height: 5, marginTop: 8 }} />
                </div>
              </button>
              <div className={s.contR}>
                <div className={s.rcLb} style={{ marginBottom: 0 }}>{v.ab.ab ? 'Tiêu đề A/B' : 'Tiêu đề'}<Tip text={v.ab.ab ? 'Chia đôi người nhận · tiêu đề mở nhiều hơn thắng · trường mới' : 'Chưa chạy A/B · bật ở bước Nội dung khi tạo chiến dịch (trường mới)'} /></div>
                {v.ab.rows.map((a) => (
                  <div key={a.k} className={s.ab}>
                    <div className={s.abTop}>
                      <span className={`${s.abK} ${a.win ? s.abWin : ''}`}>{a.k}</span>
                      <span className={s.abS}>{a.subj}</span>
                      {a.win && <span className={s.win}>{tx('cd.13')}</span>}
                    </div>
                    {sent && <div className={s.abBar} role="img" aria-label={`Tiêu đề ${a.k}: mở ${a.rate}%`}><div><div style={{ width: `${a.rate}%` }} /></div><span>{a.rate}% mở</span></div>}
                  </div>
                ))}
                <div className={s.row}>
                  <button type="button" className={s.btn} onClick={soon('Xem thử mẫu')}><IconEye size={16} aria-hidden="true" />{tx('cd.14')}</button>
                  {sent && <span className={s.tip} role="img" aria-label="Nội dung đã khoá lúc gửi lần đầu" title="Nội dung đã khoá lúc gửi lần đầu · muốn sửa thì Nhân bản"><IconLock size={16} aria-hidden="true" /></span>}
                </div>
              </div>
            </div>)}

          {sec('m8', tx('cd.t8'), <IconHistory size={18} stroke={1.9} aria-hidden="true" />, tx('cd.t8tip'), null,
            <div className={s.hist}>
              {v.hist.map((h, i) => <div key={i}><span className={s.dot} style={{ background: h.c }} /><span>{h.when}</span><span>{h.k}</span></div>)}
            </div>)}
        </div>

        <aside className={s.aside} aria-label="Cột phải chiến dịch">
          <section className={s.cardR} aria-label="Việc cần làm">
            <div className={s.headR}>
              <span className={s.hIc}><IconTarget size={18} stroke={1.9} aria-hidden="true" /></span>
              <h2 className={s.h2}>{tx('cd.15')}</h2>
              <Tip text="Việc sinh từ chiến dịch: gửi thử / lên lịch, gửi lại người lỗi, tạo cơ hội từ thư trả lời." />
              {todo.length > 0 && <span className={s.badge}>{todo.length}</span>}
              <span className={s.grow} />
              <Fold open={isOpen('rTodo')} label="Việc cần làm" onClick={tog('rTodo')} />
            </div>
            {isOpen('rTodo') && (
              <div className={s.bodyR}>
                {todo.length === 0 && <div className={s.empty}>{tx('cd.16')}</div>}
                {todoRows.map((t) => (
                  <div key={t.k} className={s.todo}>
                    <span aria-hidden="true" className={`${s.tbox} ${t.tone === 'err' ? s.tboxErr : t.tone === 'pri' ? s.tboxPri : ''}`} />
                    <div className={s.tTx}>
                      <div className={s.tK} title={t.k}>{t.k}</div>
                      <div className={s.tSub}><span className={s.av} title={t.who}>{ini(t.who)}</span><span>{t.who}</span><span style={t.tone === 'err' ? { color: 'var(--err)' } : undefined}>{t.due}</span></div>
                    </div>
                    <Button onClick={noWrite}>{t.act}</Button>
                  </div>
                ))}
                {todo.length > 4 && <Button onClick={() => setShowAll((x) => !x)}>{showAll ? 'Thu gọn' : `Xem thêm ${todo.length - 4}`}</Button>}
              </div>
            )}
          </section>

          <section className={s.cardR} aria-label="Phản hồi">
            <div className={s.headR}>
              <span className={s.hIc}><IconArrowBackUp size={18} stroke={1.9} aria-hidden="true" /></span>
              <h2 className={s.h2}>{tx('cd.17')}</h2>
              <Tip text="Thư trả lời và khách nóng; tạo cơ hội từ phản hồi." />
              <span className={s.grow} />
              <span className={s.rcSum} title="Số thư trả lời"><span><span className={s.dot} style={{ background: 'var(--d-green)' }} /><b>{v.reps.length}</b></span></span>
              <Fold open={isOpen('m7')} label="Phản hồi" onClick={tog('m7')} />
            </div>
            {isOpen('m7') && (
              <div className={s.bodyR}>
                {v.reps.length === 0 && <div className={s.empty}>{sent ? 'Chưa có thư trả lời' : 'Chưa gửi'}</div>}
                {v.reps.map((r, i) => (
                  <div key={i} className={s.rep}>
                    <span className={s.repAv} aria-hidden="true">{ini(r.sn)}</span>
                    <div className={s.repTx} title={`${r.sn} · Sale ${r.sale} · ${r.when}`}><b>{r.sn}</b><span>{r.k} · {r.when}</span></div>
                    {r.canOpp && <Button onClick={noWrite}>{tx('cd.18')}</Button>}
                    {r.hasOpp && <button type="button" className={`${s.act} ${s.actOk}`} onClick={soon('Cơ hội')}>{tx('cd.19')}</button>}
                  </div>
                ))}
                {v.hot.length > 0 && (
                  <>
                    <div className={s.hotH}><span className={s.dot} style={{ background: 'var(--d-amber)' }} />{tx('cd.20')}<Tip text="Mở từ 2 lần hoặc bấm link, chưa trả lời" /></div>
                    {v.hot.map((h) => (
                      <div key={h.sn} className={s.hot}>
                        <b>{h.sn}</b>
                        <div className={s.hotBar} role="img" aria-label={`${h.sn}: mở ${h.open}, bấm ${h.click}`}>
                          <div>
                            {h.open > 0 && <div style={{ flex: `${h.open} 1 0`, background: 'var(--d-blue)' }} />}
                            {h.click > 0 && <div style={{ flex: `${h.click} 1 0`, background: 'var(--d-amber)' }} />}
                            {6 - h.open - h.click > 0 && <div style={{ flex: `${6 - h.open - h.click} 1 0`, background: 'var(--line)' }} />}
                          </div>
                          <span><span><IconEye size={13} aria-hidden="true" />{h.open}</span><span><IconClick size={13} aria-hidden="true" />{h.click}</span></span>
                        </div>
                        <span className={s.br} />
                        <button type="button" className={`${s.act} ${s.actOut}`} onClick={mark(h.sn)}><IconArrowBackUp size={14} aria-hidden="true" />{tx('cd.21')}</button>
                        <button type="button" className={`${s.act} ${s.actPri}`} onClick={noWrite}>{h.opp ? 'Đã có cơ hội' : 'Tạo cơ hội'}</button>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </section>

          <section className={s.cardR} aria-label="Thông tin chính">
            <div className={s.headR}>
              <span className={s.hIc}><IconInfoCircle size={18} stroke={1.9} aria-hidden="true" /></span>
              <h2 className={s.h2}>{tx('cd.22')}</h2>
              <Tip text="Cài đặt gửi (trước ở khối Lịch sử & cài đặt) và trường chính của chiến dịch." />
              <span className={s.grow} />
              <Fold open={isOpen('rInfo')} label="Thông tin chính" onClick={tog('rInfo')} />
            </div>
            {isOpen('rInfo') && (
              <div className={s.bodyR}>
                {infoRows(c, d, CAMPAIGN_STATUS_LABEL[c.status]).map(([k, val], i) => (
                  <div key={k} className={s.info}><span>{k}</span><span title={val} style={i === 0 ? { fontWeight: 600 } : undefined}>{val}</span></div>
                ))}
              </div>
            )}
          </section>

          <section className={s.cardR} aria-label="Người phụ trách">
            <div className={s.headR}>
              <span className={s.hIc}><IconUser size={18} stroke={1.9} aria-hidden="true" /></span>
              <h2 className={s.h2}>{tx('cd.23')}</h2>
              <Tip text="Người tạo và Sale có khách trong danh sách nhận; avatar theo TblStaff.ImageProfile." />
              <span className={s.grow} />
              <Fold open={isOpen('rPpl')} label="Người phụ trách" onClick={tog('rPpl')} />
            </div>
            {isOpen('rPpl') && (
              <div className={s.bodyR}>
                {people.map(([u, role]) => (
                  <div key={u + role} className={s.ppl}>
                    <span className={`${s.av} ${s.av32}`} title={u}>{ini(u)}</span>
                    <div className={s.pplN}><div>{u}</div><div>{role}</div></div>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={s.cardR} aria-label="Liên kết">
            <div className={s.headR}>
              <span className={s.hIc}><IconCalendar size={18} stroke={1.9} aria-hidden="true" /></span>
              <h2 className={s.h2}>{tx('cd.24')}</h2>
              <Tip text="Màn liên quan của chiến dịch." />
              <span className={s.grow} />
              <Fold open={isOpen('rLink')} label="Liên kết" onClick={tog('rLink')} />
            </div>
            {isOpen('rLink') && (
              <div className={s.bodyR}>
                <button type="button" className={s.lnk} onClick={soon('Cơ hội')}><span className={s.lnkIc}><IconTarget size={16} aria-hidden="true" /></span><span>{tx('cd.25')}</span><span className={s.lnkEnd}>↗</span></button>
                <Link to="/ezy/customers" className={s.lnk}><span className={s.lnkIc}><IconUsers size={16} aria-hidden="true" /></span><span>{tx('cd.26')}</span><span className={s.lnkEnd}>↗</span></Link>
                <Link to="/ezy/marketing" search={{ tab: 'tpl' } as never} className={s.lnk}><span className={s.lnkIc}><IconMail size={16} aria-hidden="true" /></span><span>{tx('cd.27')}</span><span className={s.lnkEnd}>›</span></Link>
              </div>
            )}
          </section>
        </aside>
      </div>
    );
  };

  return (
    <Page kind="table">
      <PageHeader
        title={ready && c ? c.subject : 'Chiến dịch'}
        lead={
          <>
            <button type="button" className={s.back} aria-label="Quay lại danh sách chiến dịch" title="Quay lại danh sách chiến dịch" onClick={back}><IconArrowLeft size={18} stroke={1.9} aria-hidden="true" /></button>
            {c && <span className={s.code}>{c.id}</span>}
          </>
        }
        meta={ready && c ? <Chip tone={c.status === 'DONE' ? 'ok' : c.status === 'PAUSED' ? 'warn' : c.status === 'DRAFT' ? 'neutral' : 'info'}>{CAMPAIGN_STATUS_LABEL[c.status]}</Chip> : undefined}
        sub={
          ready && c && d ? (
            <span className={s.subRow}>
              <span className={s.metaI} title="Bắt đầu gửi"><IconCalendar size={14} aria-hidden="true" />{d.start}</span>
              <span className={s.metaI} title="Người tạo"><IconUser size={14} aria-hidden="true" />{c.owner}</span>
              <span className={s.metaI} title="Khách"><IconUsers size={14} aria-hidden="true" />{d.customers}</span>
            </span>
          ) : undefined
        }
        actions={
          <>
            <HeaderTool label="Thao tác khác" icon={IconDots} onClick={soon('Menu thao tác')} />
            <Button variant="primary" icon={<IconSend size={16} aria-hidden="true" />} onClick={noWrite}>{c?.status === 'DRAFT' ? 'Lên lịch' : c?.status === 'DONE' || !c ? 'Nhân bản' : c.status === 'PAUSED' ? 'Tiếp tục' : 'Tạm dừng'}</Button>
          </>
        }
      />
      <StateView
        state={state}
        title={state === 'empty' ? 'Không tìm thấy chiến dịch' : undefined}
        icon={state === 'empty' ? <IconMail size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => void camp.refetch()}>{tx('cd.28')}</Button> : state === 'empty' ? <Button onClick={back}>{tx('cd.29')}</Button> : undefined}
        skeleton={<Skeleton rows={8} />}
      >
        {body()}
      </StateView>
    </Page>
  );
}
